import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/auth/AuthContext';
import CreateAgentForm from '../../components/CreateAgentForm';

const AdminDashboard = () => {
  // Get authentication state and user data from auth context
  const { userEmail: authUserEmail, userId } = useAuth();
  
  // Get userEmail and userId from localStorage as fallback (same pattern as Agents.jsx)
  const storedUser = JSON.parse(localStorage.getItem('chatbotUser') || 'null');
  const finalUserId = userId || storedUser?.userId || storedUser?.id || null;
  
  // Get userEmail - prefer auth context, fallback to localStorage
  const userEmail = authUserEmail || storedUser?.email || localStorage.getItem('userEmail') || '';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryStats, setSummaryStats] = useState({
    totalUsers: 0,
    totalAgents: 0,
    totalPhoneNumbers: 0,
    totalKnowledgeBases: 0,
    totalCalls: 0,
    totalRevenue: 0
  });
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showPDFPopup, setShowPDFPopup] = useState(false);
  const [threeMonthView, setThreeMonthView] = useState(false);
  const [threeMonthBillingData, setThreeMonthBillingData] = useState(null);
  const [threeMonthLoading, setThreeMonthLoading] = useState(false);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);

  // Check if user is admin
  const isAdmin = () => {
    // Try to get email from chatbotUser object first (new format)
    const chatbotUser = localStorage.getItem('chatbotUser');
    let userEmail = '';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        userEmail = user.email || '';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    // Fallback to old format (direct userEmail key)
    if (!userEmail) {
      userEmail = localStorage.getItem('userEmail') || '';
    }
    
    return userEmail.toLowerCase() === 'info@urcommunity.com.au';
  };

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    fetchAllUsersData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('[data-dropdown-container]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchAllUsersData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get email from chatbotUser object (new format)
      const chatbotUser = localStorage.getItem('chatbotUser');
      let userEmail = '';
      
      if (chatbotUser) {
        try {
          const user = JSON.parse(chatbotUser);
          userEmail = user.email || '';
        } catch (e) {
          console.error('Error parsing chatbotUser:', e);
        }
      }
      
      // Fallback to old format (direct userEmail key)
      if (!userEmail) {
        userEmail = localStorage.getItem('userEmail') || '';
      }
      
      // Fetch dashboard statistics
      const statsResponse = await fetch(`${API_BASE_URL}/api/admin/statistics?email=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      if (!statsResponse.ok) {
        throw new Error(`HTTP error! status: ${statsResponse.status}`);
      }

      const statsData = await statsResponse.json();
      if (statsData.success) {
        // Update the summary statistics with the fetched data
        setSummaryStats({
          totalUsers: statsData.data.totalUsers,
          totalAgents: statsData.data.totalAgents,
          totalPhoneNumbers: statsData.data.totalPhoneNumbers,
          totalKnowledgeBases: statsData.data.totalKnowledgeBases,
          totalCalls: statsData.data.totalCalls,
          totalRevenue: statsData.data.totalRevenue
        });
      } else {
        throw new Error(statsData.error || 'Failed to fetch dashboard statistics');
      }

      // Fetch detailed user data
      const usersResponse = await fetch(`${API_BASE_URL}/api/admin/all-users?email=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-email': userEmail
        }
      });

      if (!usersResponse.ok) {
        throw new Error(`HTTP error! status: ${usersResponse.status}`);
      }

      const usersData = await usersResponse.json();
      if (usersData.success) {
        setUsers(usersData.users || []);
      } else {
        throw new Error(usersData.error || 'Failed to fetch users data');
      }
    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
        setError('Backend server not running. Please start the server on port 3001.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch combined billing data for all users
  const fetchAllUsersBilling = async () => {
    setBillingLoading(true);
    setBillingError(null);

    try {
      const billingUrl = `${API_BASE_URL}/api/admin/billing/original-doubled?month=${month}&year=${year}`;
      console.log('🔍 [ADMIN-DASHBOARD] Fetching all users billing data from:', billingUrl);
      
      const billingResponse = await fetch(billingUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!billingResponse.ok) {
        const errorText = await billingResponse.text();
        console.error('❌ [ADMIN-DASHBOARD] All Users Billing API error:', {
          status: billingResponse.status,
          statusText: billingResponse.statusText,
          url: billingUrl,
          response: errorText
        });
        throw new Error(`HTTP error! status: ${billingResponse.status} - ${errorText || billingResponse.statusText}`);
      }

      const billingResult = await billingResponse.json();
      if (billingResult.success && billingResult.data.totals) {
        // Calculate expected period (starts in selected month)
        const expectedStart = `${year}-${String(month).padStart(2, '0')}-06`;
        const expectedEndMonth = month === 12 ? 1 : month + 1;
        const expectedEndYear = month === 12 ? year + 1 : year;
        const expectedEnd = `${expectedEndYear}-${String(expectedEndMonth).padStart(2, '0')}-05`;
        
        console.log('✅ [ADMIN-DASHBOARD] All users billing data received:', {
          selectedMonth: month,
          selectedMonthName: new Date(2000, month - 1).toLocaleString('en-AU', { month: 'long' }),
          selectedYear: year,
          receivedPeriod: billingResult.data.period,
          expectedStart,
          expectedEnd
        });
        // Format the totals data to match the single user billing structure
        setBillingData({
          ...billingResult.data.totals,
          billingPeriod: billingResult.data.period,
          counts: {
            calls: billingResult.data.users.reduce((sum, u) => sum + (u.counts?.calls || 0), 0),
            chats: billingResult.data.users.reduce((sum, u) => sum + (u.counts?.chats || 0), 0),
            phones: billingResult.data.users.reduce((sum, u) => sum + (u.counts?.phones || 0), 0)
          }
        });
      } else {
        throw new Error(billingResult.message || 'Failed to fetch all users billing data');
      }
    } catch (err) {
      setBillingError(err.message);
      console.error('Error fetching all users billing:', err);
    } finally {
      setBillingLoading(false);
    }
  };

  // Fetch user details and billing data when a user is selected
  const fetchUserDetailsAndBilling = async (userEmail) => {
    if (!userEmail) {
      setSelectedUser(null);
      setSelectedUserDetails(null);
      setBillingData(null);
      return;
    }

    setBillingLoading(true);
    setBillingError(null);

    try {
      // Find user from the users list
      const user = users.find(u => u.email.toLowerCase() === userEmail.toLowerCase());
      if (!user) {
        throw new Error('User not found');
      }

      setSelectedUser(user);

      // Fetch user details (if needed from a separate endpoint)
      setSelectedUserDetails({
        id: user.id,
        email: user.email,
        fullName: user.full_name || 'N/A',
        phone: user.phone || 'N/A',
        createdAt: user.created_at,
        agentCount: user.agent_count || 0,
        phoneCount: user.phone_count || 0,
        kbCount: user.kb_count || 0,
        callCount: user.call_count || 0
      });

      // Fetch billing data
      if (!user.id) {
        throw new Error('User ID is missing');
      }

      const billingUrl = `${API_BASE_URL}/api/admin/billing/original-doubled/${encodeURIComponent(user.id)}?month=${month}&year=${year}`;
      console.log('🔍 [ADMIN-DASHBOARD] Fetching billing data from:', billingUrl);
      console.log('🔍 [ADMIN-DASHBOARD] Month/Year being sent:', { month, year, monthName: new Date(2000, month - 1).toLocaleString('en-AU', { month: 'long' }) });
      
      const billingResponse = await fetch(billingUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!billingResponse.ok) {
        const errorText = await billingResponse.text();
        console.error('❌ [ADMIN-DASHBOARD] Billing API error:', {
          status: billingResponse.status,
          statusText: billingResponse.statusText,
          url: billingUrl,
          response: errorText
        });
        throw new Error(`HTTP error! status: ${billingResponse.status} - ${errorText || billingResponse.statusText}`);
      }

      const billingResult = await billingResponse.json();
      if (billingResult.success) {
        // Calculate expected period (starts in selected month)
        const expectedStart = `${year}-${String(month).padStart(2, '0')}-06`;
        const expectedEndMonth = month === 12 ? 1 : month + 1;
        const expectedEndYear = month === 12 ? year + 1 : year;
        const expectedEnd = `${expectedEndYear}-${String(expectedEndMonth).padStart(2, '0')}-05`;
        
        console.log('✅ [ADMIN-DASHBOARD] Billing data received:', {
          selectedMonth: month,
          selectedMonthName: new Date(2000, month - 1).toLocaleString('en-AU', { month: 'long' }),
          selectedYear: year,
          receivedPeriod: billingResult.data.billingPeriod,
          expectedStart,
          expectedEnd
        });
        setBillingData(billingResult.data);
      } else {
        throw new Error(billingResult.message || 'Failed to fetch billing data');
      }
    } catch (err) {
      setBillingError(err.message);
      console.error('Error fetching user details:', err);
    } finally {
      setBillingLoading(false);
    }
  };

  // Fetch 3 months of billing data for a user
  const fetchThreeMonthsBilling = async (userEmail) => {
    if (!userEmail) {
      setThreeMonthBillingData(null);
      return;
    }

    setThreeMonthLoading(true);

    try {
      // Find user from the users list
      const user = users.find(u => u.email.toLowerCase() === userEmail.toLowerCase());
      if (!user || !user.id) {
        throw new Error('User not found');
      }

      // Calculate 3 months starting from selected month
      const months = [];
      for (let i = 0; i < 3; i++) {
        let currentMonth = month + i;
        let currentYear = year;
        
        // Handle year rollover
        while (currentMonth > 12) {
          currentMonth -= 12;
          currentYear += 1;
        }
        
        months.push({ month: currentMonth, year: currentYear });
      }

      // Fetch billing data for each month
      const billingPromises = months.map(({ month: m, year: y }) => {
        const billingUrl = `${API_BASE_URL}/api/admin/billing/original-doubled/${encodeURIComponent(user.id)}?month=${m}&year=${y}`;
        return fetch(billingUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(res => res.json());
      });

      const billingResults = await Promise.all(billingPromises);
      
      // Process results
      const threeMonthData = billingResults.map((result, index) => {
        const { month: m, year: y } = months[index];
        return {
          month: m,
          year: y,
          monthName: new Date(2000, m - 1).toLocaleString('en-AU', { month: 'long' }),
          success: result.success,
          data: result.data || null,
          error: result.success ? null : (result.message || 'Failed to fetch data')
        };
      });

      setThreeMonthBillingData(threeMonthData);
    } catch (err) {
      console.error('Error fetching 3 months billing data:', err);
      setThreeMonthBillingData(null);
    } finally {
      setThreeMonthLoading(false);
    }
  };

  // Handle user selection from search
  const handleUserSelect = (email) => {
    if (email === 'ALL') {
      setSearchTerm('ALL');
      setSelectedUser(null);
      setSelectedUserDetails(null);
      setShowAllUsers(true);
      setShowDropdown(false);
      // Don't fetch automatically - wait for Submit button
      return;
    }
    // Find and set the selected user
    const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (user) {
      setSelectedUser(user);
      setSelectedUserDetails({
        id: user.id,
        email: user.email,
        fullName: user.full_name || 'N/A',
        phone: user.phone || 'N/A',
        createdAt: user.created_at,
        agentCount: user.agent_count || 0,
        phoneCount: user.phone_count || 0,
        kbCount: user.kb_count || 0,
        callCount: user.call_count || 0
      });
    }
    setSearchTerm(email);
    setShowAllUsers(false);
    setShowDropdown(false);
    // Don't fetch billing data automatically - wait for Submit button
    // Clear billing data when user changes
    setBillingData(null);
  };

  // Filter users for search dropdown
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }).slice(0, 10); // Limit to 10 results for dropdown


  const exportToCSV = () => {
    const headers = ['Email', 'Full Name', 'Phone', 'Agents', 'Phone Numbers', 'Knowledge Bases', 'Calls', 'Total Amount', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...users.map(user => [
        user.email,
        user.full_name || '',
        user.phone || '',
        user.agent_count || 0,
        user.phone_count || 0,
        user.kb_count || 0,
        user.call_count || 0,
        user.total_amount || 0,
        user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = (forUser = false) => {
    try {
      // Check if a specific user is selected with billing data (single month or 3 months)
      if (selectedUserDetails && !showAllUsers && (billingData || (threeMonthView && threeMonthBillingData))) {
        // If 3-month view is enabled, generate PDF with 3 months data
        if (threeMonthView && threeMonthBillingData && threeMonthBillingData.length > 0) {
          const printWindow = window.open('', '_blank');
          const timestamp = new Date().toISOString();
          const dateStr = new Date().toISOString().split('T')[0];
          
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>User Billing Report - 3 Months - ${selectedUserDetails.email} - ${dateStr}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  padding: 20px;
                  color: #333;
                }
                h1 {
                  color: #222;
                  border-bottom: 2px solid #dc2626;
                  padding-bottom: 10px;
                }
                h2 {
                  color: #444;
                  margin-top: 30px;
                  border-bottom: 1px solid #ddd;
                  padding-bottom: 5px;
                }
                h3 {
                  color: #555;
                  margin-top: 20px;
                  font-size: 1.1rem;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin: 20px 0;
                }
                th, td {
                  border: 1px solid #ddd;
                  padding: 8px;
                  text-align: left;
                }
                th {
                  background-color: #f8f9fa;
                  font-weight: bold;
                }
                tr:nth-child(even) {
                  background-color: #f9f9f9;
                }
                .info-grid {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  gap: 15px;
                  margin: 20px 0;
                }
                .info-card {
                  border: 1px solid #ddd;
                  padding: 15px;
                  border-radius: 5px;
                  background-color: #f8f9fa;
                }
                .info-label {
                  font-size: 12px;
                  color: #666;
                  margin-bottom: 5px;
                }
                .info-value {
                  font-size: 16px;
                  font-weight: 500;
                  color: #222;
                  word-break: break-all;
                }
                .billing-grid {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  gap: 15px;
                  margin: 20px 0;
                }
                .billing-card {
                  border: 1px solid #ddd;
                  padding: 15px;
                  border-radius: 5px;
                  background-color: white;
                }
                .billing-label {
                  font-size: 14px;
                  font-weight: 600;
                  color: #1f2937;
                  margin-bottom: 12px;
                }
                .billing-row {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
                  font-size: 13px;
                }
                .billing-value {
                  font-weight: 500;
                }
                .month-section {
                  border: 1px solid #ddd;
                  border-radius: 8px;
                  padding: 20px;
                  margin-bottom: 30px;
                  background-color: #f8f9fa;
                }
                .timestamp {
                  color: #666;
                  font-size: 12px;
                  margin-top: 20px;
                }
              </style>
            </head>
            <body>
              <h1>User Billing Report - 3 Months</h1>
              <div class="timestamp">Generated: ${new Date(timestamp).toLocaleString()}</div>
              
              <h2>User Details</h2>
              <div class="info-grid">
                <div class="info-card">
                  <div class="info-label">User ID</div>
                  <div class="info-value">${selectedUserDetails.id || 'N/A'}</div>
                </div>
                <div class="info-card">
                  <div class="info-label">Email</div>
                  <div class="info-value">${selectedUserDetails.email || 'N/A'}</div>
                </div>
                <div class="info-card">
                  <div class="info-label">Full Name</div>
                  <div class="info-value">${selectedUserDetails.fullName || 'N/A'}</div>
                </div>
                <div class="info-card">
                  <div class="info-label">Joined Date</div>
                  <div class="info-value">${formatDate(selectedUserDetails.createdAt)}</div>
                </div>
              </div>
              
              <h2>3 Months Billing Summary</h2>
              ${threeMonthBillingData.map((monthData, index) => `
                <div class="month-section">
                  <h3>${monthData.monthName} ${monthData.year}${monthData.data?.billingPeriod ? ` (${monthData.data.billingPeriod.start} to ${monthData.data.billingPeriod.end})` : ''}</h3>
                  ${monthData.success && monthData.data ? `
                    <div class="billing-grid">
                      ${forUser ? `
                        <!-- For User: Show only Doubled, use actual names -->
                        <div class="billing-card">
                          <div class="billing-label">Voice Calls</div>
                          <div class="billing-row">
                            <span>Amount:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.doubled?.componentA || 0)}</span>
                          </div>
                          <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                            ${monthData.data.counts?.calls || 0} calls
                          </div>
                        </div>
                        
                        <div class="billing-card">
                          <div class="billing-label">Chat Sessions</div>
                          <div class="billing-row">
                            <span>Amount:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.doubled?.componentB || 0)}</span>
                          </div>
                          <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                            ${monthData.data.counts?.chats || 0} chats
                          </div>
                        </div>
                        
                        <div class="billing-card">
                          <div class="billing-label">Phone Numbers</div>
                          <div class="billing-row">
                            <span>Amount:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.doubled?.componentC || 0)}</span>
                          </div>
                          <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                            ${monthData.data.counts?.phones || 0} phones
                          </div>
                        </div>
                        
                        <div class="billing-card" style="background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white;">
                          <div class="billing-label" style="color: white;">Total</div>
                          <div class="billing-row">
                            <span>Amount:</span>
                            <span class="billing-value" style="font-size: 16px; font-weight: 600;">${formatCurrency(monthData.data.doubled?.total || 0)}</span>
                          </div>
                        </div>
                      ` : `
                        <!-- For Myself: Show Original and Doubled, use Component A/B/C names -->
                        <div class="billing-card">
                          <div class="billing-label">Component A (Voice Calls)</div>
                          <div class="billing-row">
                            <span>Original:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.original?.componentA || 0)}</span>
                          </div>
                          <div class="billing-row">
                            <span>Doubled:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.doubled?.componentA || 0)}</span>
                          </div>
                          <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                            ${monthData.data.counts?.calls || 0} calls
                          </div>
                        </div>
                        
                        <div class="billing-card">
                          <div class="billing-label">Component B (Chat Sessions)</div>
                          <div class="billing-row">
                            <span>Original:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.original?.componentB || 0)}</span>
                          </div>
                          <div class="billing-row">
                            <span>Doubled:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.doubled?.componentB || 0)}</span>
                          </div>
                          <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                            ${monthData.data.counts?.chats || 0} chats
                          </div>
                        </div>
                        
                        <div class="billing-card">
                          <div class="billing-label">Component C (Phone Numbers)</div>
                          <div class="billing-row">
                            <span>Original:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.original?.componentC || 0)}</span>
                          </div>
                          <div class="billing-row">
                            <span>Doubled:</span>
                            <span class="billing-value">${formatCurrency(monthData.data.doubled?.componentC || 0)}</span>
                          </div>
                          <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                            ${monthData.data.counts?.phones || 0} phones
                          </div>
                        </div>
                        
                        <div class="billing-card" style="background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white;">
                          <div class="billing-label" style="color: white;">Total</div>
                          <div class="billing-row">
                            <span>Original:</span>
                            <span class="billing-value" style="font-size: 16px; font-weight: 600;">${formatCurrency(monthData.data.original?.total || 0)}</span>
                          </div>
                          <div class="billing-row">
                            <span>Doubled:</span>
                            <span class="billing-value" style="font-size: 16px; font-weight: 600;">${formatCurrency(monthData.data.doubled?.total || 0)}</span>
                          </div>
                        </div>
                      `}
                    </div>
                    
                    ${monthData.data.componentAInternal && !forUser ? `
                      <h4 style="margin-top: 20px; font-size: 1rem; font-weight: 600; color: #1f2937;">Voice Calls Internal Billing Breakdown</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>Component</th>
                            <th>Original</th>
                            <th>Doubled</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${Object.entries(monthData.data.componentAInternal.original).map(([key, value]) => `
                            <tr>
                              <td>${key.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase())}</td>
                              <td>${formatCurrency(value || 0)}</td>
                              <td>${formatCurrency(monthData.data.componentAInternal.doubled[key] || 0)}</td>
                            </tr>
                          `).join('')}
                        </tbody>
                      </table>
                    ` : monthData.data.componentAInternal && forUser ? `
                      <h4 style="margin-top: 20px; font-size: 1rem; font-weight: 600; color: #1f2937;">Voice Calls Internal Billing Breakdown</h4>
                      <div class="billing-grid">
                        ${Object.entries(monthData.data.componentAInternal.original).map(([key, value]) => `
                          <div class="billing-card">
                            <div class="billing-label">${key.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase())}</div>
                            <div class="billing-row">
                              <span>Amount:</span>
                              <span class="billing-value">${formatCurrency(monthData.data.componentAInternal.doubled[key] || 0)}</span>
                            </div>
                          </div>
                        `).join('')}
                      </div>
                    ` : ''}
                  ` : `
                    <div style="padding: 16px; background: #fee2e2; border-radius: 8px; color: #dc2626; font-size: 14px;">
                      ${monthData.error || 'No data available for this month'}
                    </div>
                  `}
                </div>
              `).join('')}
              
              ${threeMonthBillingData.every(m => m.success && m.data) ? `
                <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); border-radius: 12px; padding: 20px; color: white; margin-top: 30px;">
                  <h3 style="color: white; margin: 0 0 16px 0;">3 Months Total Summary</h3>
                  <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
                    <div>
                      <div style="font-size: 12px; opacity: 0.9; margin-bottom: 4px;">Total Original (3 Months)</div>
                      <div style="font-size: 20px; font-weight: 600;">
                        ${formatCurrency(threeMonthBillingData.reduce((sum, m) => sum + (m.data?.original?.total || 0), 0))}
                      </div>
                    </div>
                    <div>
                      <div style="font-size: 12px; opacity: 0.9; margin-bottom: 4px;">Total Doubled (3 Months)</div>
                      <div style="font-size: 20px; font-weight: 600;">
                        ${formatCurrency(threeMonthBillingData.reduce((sum, m) => sum + (m.data?.doubled?.total || 0), 0))}
                      </div>
                    </div>
                  </div>
                </div>
              ` : ''}
            </body>
            </html>
          `);
          
          printWindow.document.close();
          
          setTimeout(() => {
            printWindow.print();
          }, 250);
          return;
        }
        
        // Single month PDF generation (existing code)
        if (billingData) {
          // Generate PDF for specific user with billing details
          const printWindow = window.open('', '_blank');
          const timestamp = new Date().toISOString();
          const dateStr = new Date().toISOString().split('T')[0];
          const monthName = new Date(2000, month - 1).toLocaleString('en-AU', { month: 'long' });
          
          printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>User Billing Report - ${selectedUserDetails.email} - ${dateStr}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
              }
              h1 {
                color: #222;
                border-bottom: 2px solid #dc2626;
                padding-bottom: 10px;
              }
              h2 {
                color: #444;
                margin-top: 30px;
                border-bottom: 1px solid #ddd;
                padding-bottom: 5px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f8f9fa;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .info-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
                margin: 20px 0;
              }
              .info-card {
                border: 1px solid #ddd;
                padding: 15px;
                border-radius: 5px;
                background-color: #f8f9fa;
              }
              .info-label {
                font-size: 12px;
                color: #666;
                margin-bottom: 5px;
              }
              .info-value {
                font-size: 16px;
                font-weight: 500;
                color: #222;
                word-break: break-all;
              }
              .billing-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
                margin: 20px 0;
              }
              .billing-card {
                border: 1px solid #ddd;
                padding: 15px;
                border-radius: 5px;
                background-color: white;
              }
              .billing-label {
                font-size: 14px;
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 12px;
              }
              .billing-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 8px;
                font-size: 13px;
              }
              .billing-value {
                font-weight: 500;
              }
              .status-badge {
                display: inline-block;
                padding: 4px 12px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 500;
                margin-left: 8px;
              }
              .status-paid {
                background-color: #d1fae5;
                color: #065f46;
              }
              .status-pending {
                background-color: #fef3c7;
                color: #92400e;
              }
              .timestamp {
                color: #666;
                font-size: 12px;
                margin-top: 20px;
              }
            </style>
          </head>
          <body>
            <h1>User Billing Report</h1>
            <div class="timestamp">Generated: ${new Date(timestamp).toLocaleString()}</div>
            
            <h2>User Details</h2>
            <div class="info-grid">
              <div class="info-card">
                <div class="info-label">User ID</div>
                <div class="info-value">${selectedUserDetails.id || 'N/A'}</div>
              </div>
              <div class="info-card">
                <div class="info-label">Email</div>
                <div class="info-value">${selectedUserDetails.email || 'N/A'}</div>
              </div>
              <div class="info-card">
                <div class="info-label">Full Name</div>
                <div class="info-value">${selectedUserDetails.fullName || 'N/A'}</div>
              </div>
              <div class="info-card">
                <div class="info-label">Joined Date</div>
                <div class="info-value">${formatDate(selectedUserDetails.createdAt)}</div>
              </div>
              <div class="info-card">
                <div class="info-label">Agents</div>
                <div class="info-value">${selectedUserDetails.agentCount || 0}</div>
              </div>
              <div class="info-card">
                <div class="info-label">Phone Numbers</div>
                <div class="info-value">${selectedUserDetails.phoneCount || 0}</div>
              </div>
            </div>
            
            <h2>Billing Period</h2>
            <div class="info-card">
              <div class="info-label">Period</div>
              <div class="info-value">${billingData.billingPeriod?.start || 'N/A'} to ${billingData.billingPeriod?.end || 'N/A'}</div>
            </div>
            <div class="info-card">
              <div class="info-label">Selected Month/Year</div>
              <div class="info-value">${monthName} ${year}</div>
            </div>
            
            <h2>Billing Summary</h2>
            <div class="billing-grid">
              ${forUser ? `
                <!-- For User: Show only Doubled, use actual names -->
                <div class="billing-card">
                  <div class="billing-label">Voice Calls</div>
                  <div class="billing-row">
                    <span>Amount:</span>
                    <span class="billing-value">${formatCurrency(billingData.doubled?.componentA || 0)}</span>
                  </div>
                  <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                    ${billingData.counts?.calls || 0} calls
                  </div>
                </div>
                
                <div class="billing-card">
                  <div class="billing-label">Chat Sessions</div>
                  <div class="billing-row">
                    <span>Amount:</span>
                    <span class="billing-value">${formatCurrency(billingData.doubled?.componentB || 0)}</span>
                  </div>
                  <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                    ${billingData.counts?.chats || 0} chats
                  </div>
                </div>
                
                <div class="billing-card">
                  <div class="billing-label">Phone Numbers</div>
                  <div class="billing-row">
                    <span>Amount:</span>
                    <span class="billing-value">${formatCurrency(billingData.doubled?.componentC || 0)}</span>
                  </div>
                  <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                    ${billingData.counts?.phones || 0} phones
                  </div>
                </div>
                
                <div class="billing-card" style="background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white;">
                  <div class="billing-label" style="color: white;">Total</div>
                  <div class="billing-row">
                    <span>Amount:</span>
                    <span class="billing-value" style="font-size: 16px; font-weight: 600;">${formatCurrency(billingData.doubled?.total || 0)}</span>
                  </div>
                </div>
                
                <div class="billing-card">
                  <div class="billing-label">Billing Status</div>
                  <div style="margin-top: 8px;">
                    <span style="font-size: 14px; font-weight: 500; text-transform: capitalize;">
                      ${billingData.billingStatus || 'Pending'}
                    </span>
                    <span class="status-badge ${(billingData.billingStatus === 'PAID' || billingData.billingStatus === 'paid') ? 'status-paid' : 'status-pending'}">
                      ${(billingData.billingStatus === 'PAID' || billingData.billingStatus === 'paid') ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                  ${billingData.paymentIntentId ? `
                    <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                      Payment ID: ${billingData.paymentIntentId}
                    </div>
                  ` : ''}
                </div>
              ` : `
                <!-- For Myself: Show Original and Doubled, use Component A/B/C names -->
                <div class="billing-card">
                  <div class="billing-label">Component A (Voice Calls)</div>
                  <div class="billing-row">
                    <span>Original:</span>
                    <span class="billing-value">${formatCurrency(billingData.original?.componentA || 0)}</span>
                  </div>
                  <div class="billing-row">
                    <span>Doubled:</span>
                    <span class="billing-value">${formatCurrency(billingData.doubled?.componentA || 0)}</span>
                  </div>
                  <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                    ${billingData.counts?.calls || 0} calls
                  </div>
                </div>
                
                <div class="billing-card">
                  <div class="billing-label">Component B (Chat Sessions)</div>
                  <div class="billing-row">
                    <span>Original:</span>
                    <span class="billing-value">${formatCurrency(billingData.original?.componentB || 0)}</span>
                  </div>
                  <div class="billing-row">
                    <span>Doubled:</span>
                    <span class="billing-value">${formatCurrency(billingData.doubled?.componentB || 0)}</span>
                  </div>
                  <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                    ${billingData.counts?.chats || 0} chats
                  </div>
                </div>
                
                <div class="billing-card">
                  <div class="billing-label">Component C (Phone Numbers)</div>
                  <div class="billing-row">
                    <span>Original:</span>
                    <span class="billing-value">${formatCurrency(billingData.original?.componentC || 0)}</span>
                  </div>
                  <div class="billing-row">
                    <span>Doubled:</span>
                    <span class="billing-value">${formatCurrency(billingData.doubled?.componentC || 0)}</span>
                  </div>
                  <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                    ${billingData.counts?.phones || 0} phones
                  </div>
                </div>
                
                <div class="billing-card" style="background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white;">
                  <div class="billing-label" style="color: white;">Total</div>
                  <div class="billing-row">
                    <span>Original:</span>
                    <span class="billing-value" style="font-size: 16px; font-weight: 600;">${formatCurrency(billingData.original?.total || 0)}</span>
                  </div>
                  <div class="billing-row">
                    <span>Doubled:</span>
                    <span class="billing-value" style="font-size: 16px; font-weight: 600;">${formatCurrency(billingData.doubled?.total || 0)}</span>
                  </div>
                </div>
                
                <div class="billing-card">
                  <div class="billing-label">Billing Status</div>
                  <div style="margin-top: 8px;">
                    <span style="font-size: 14px; font-weight: 500; text-transform: capitalize;">
                      ${billingData.billingStatus || 'Pending'}
                    </span>
                    <span class="status-badge ${(billingData.billingStatus === 'PAID' || billingData.billingStatus === 'paid') ? 'status-paid' : 'status-pending'}">
                      ${(billingData.billingStatus === 'PAID' || billingData.billingStatus === 'paid') ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                  ${billingData.paymentIntentId ? `
                    <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                      Payment ID: ${billingData.paymentIntentId}
                    </div>
                  ` : ''}
                </div>
              `}
            </div>
            
            ${billingData.componentAInternal ? `
              <h2>Voice Calls Internal Billing Breakdown</h2>
              ${forUser ? `
                <!-- For User: Show only Doubled amounts -->
                <div class="billing-grid">
                  ${Object.entries(billingData.componentAInternal.original).map(([key, value]) => `
                    <div class="billing-card">
                      <div class="billing-label">${key.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase())}</div>
                      <div class="billing-row">
                        <span>Amount:</span>
                        <span class="billing-value">${formatCurrency(billingData.componentAInternal.doubled[key] || 0)}</span>
                      </div>
                    </div>
                  `).join('')}
                </div>
              ` : `
                <!-- For Myself: Show Original and Doubled -->
                <table>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Original</th>
                      <th>Doubled</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${Object.entries(billingData.componentAInternal.original).map(([key, value]) => `
                      <tr>
                        <td>${key.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase())}</td>
                        <td>${formatCurrency(value || 0)}</td>
                        <td>${formatCurrency(billingData.componentAInternal.doubled[key] || 0)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              `}
            ` : ''}
          </body>
          </html>
        `);
        
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.print();
        }, 250);
      } else {
        // Generate PDF for general dashboard (all users)
        const printWindow = window.open('', '_blank');
        const timestamp = new Date().toISOString();
        const dateStr = new Date().toISOString().split('T')[0];
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Admin Dashboard Report - ${dateStr}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
              }
              h1 {
                color: #222;
                border-bottom: 2px solid #dc2626;
                padding-bottom: 10px;
              }
              h2 {
                color: #444;
                margin-top: 30px;
                border-bottom: 1px solid #ddd;
                padding-bottom: 5px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f8f9fa;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .stats {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
                margin: 20px 0;
              }
              .stat-card {
                border: 1px solid #ddd;
                padding: 15px;
                border-radius: 5px;
                background-color: #f8f9fa;
              }
              .stat-label {
                font-size: 12px;
                color: #666;
                margin-bottom: 5px;
              }
              .stat-value {
                font-size: 24px;
                font-weight: bold;
                color: #222;
              }
              .timestamp {
                color: #666;
                font-size: 12px;
                margin-top: 20px;
              }
            </style>
          </head>
          <body>
            <h1>Admin Dashboard Report</h1>
            <div class="timestamp">Generated: ${new Date(timestamp).toLocaleString()}</div>
            
            <h2>Summary Statistics</h2>
            <div class="stats">
              <div class="stat-card">
                <div class="stat-label">Total Users</div>
                <div class="stat-value">${summaryStats.totalUsers}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Agents</div>
                <div class="stat-value">${summaryStats.totalAgents}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Phone Numbers</div>
                <div class="stat-value">${summaryStats.totalPhoneNumbers}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Knowledge Bases</div>
                <div class="stat-value">${summaryStats.totalKnowledgeBases}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Calls</div>
                <div class="stat-value">${summaryStats.totalCalls}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Revenue</div>
                <div class="stat-value">${formatCurrency(summaryStats.totalRevenue)}</div>
              </div>
            </div>
            
            <h2>Users List (${users.length} users)</h2>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Full Name</th>
                  <th>Phone</th>
                  <th>Agents</th>
                  <th>Phone Numbers</th>
                  <th>Knowledge Bases</th>
                  <th>Calls</th>
                  <th>Total Amount</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                ${users.map(user => `
                  <tr>
                    <td>${user.email || 'N/A'}</td>
                    <td>${user.full_name || 'N/A'}</td>
                    <td>${user.phone || 'N/A'}</td>
                    <td>${user.agent_count || 0}</td>
                    <td>${user.phone_count || 0}</td>
                    <td>${user.kb_count || 0}</td>
                    <td>${user.call_count || 0}</td>
                    <td>${formatCurrency(user.total_amount || 0)}</td>
                    <td>${user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
          </html>
        `);
        
        printWindow.document.close();
        
          setTimeout(() => {
            printWindow.print();
          }, 250);
        }
      } else {
        // Generate PDF for general dashboard (all users)
        const printWindow = window.open('', '_blank');
        const timestamp = new Date().toISOString();
        const dateStr = new Date().toISOString().split('T')[0];
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Admin Dashboard Report - ${dateStr}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
              }
              h1 {
                color: #222;
                border-bottom: 2px solid #dc2626;
                padding-bottom: 10px;
              }
              h2 {
                color: #444;
                margin-top: 30px;
                border-bottom: 1px solid #ddd;
                padding-bottom: 5px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f8f9fa;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .stats {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
                margin: 20px 0;
              }
              .stat-card {
                border: 1px solid #ddd;
                padding: 15px;
                border-radius: 5px;
                background-color: #f8f9fa;
              }
              .stat-label {
                font-size: 12px;
                color: #666;
                margin-bottom: 5px;
              }
              .stat-value {
                font-size: 24px;
                font-weight: bold;
                color: #222;
              }
              .timestamp {
                color: #666;
                font-size: 12px;
                margin-top: 20px;
              }
            </style>
          </head>
          <body>
            <h1>Admin Dashboard Report</h1>
            <div class="timestamp">Generated: ${new Date(timestamp).toLocaleString()}</div>
            
            <h2>Summary Statistics</h2>
            <div class="stats">
              <div class="stat-card">
                <div class="stat-label">Total Users</div>
                <div class="stat-value">${summaryStats.totalUsers}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Agents</div>
                <div class="stat-value">${summaryStats.totalAgents}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Phone Numbers</div>
                <div class="stat-value">${summaryStats.totalPhoneNumbers}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Knowledge Bases</div>
                <div class="stat-value">${summaryStats.totalKnowledgeBases}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Calls</div>
                <div class="stat-value">${summaryStats.totalCalls}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Total Revenue</div>
                <div class="stat-value">${formatCurrency(summaryStats.totalRevenue)}</div>
              </div>
            </div>
            
            <h2>Users List (${users.length} users)</h2>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Full Name</th>
                  <th>Phone</th>
                  <th>Agents</th>
                  <th>Phone Numbers</th>
                  <th>Knowledge Bases</th>
                  <th>Calls</th>
                  <th>Total Amount</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                ${users.map(user => `
                  <tr>
                    <td>${user.email || 'N/A'}</td>
                    <td>${user.full_name || 'N/A'}</td>
                    <td>${user.phone || 'N/A'}</td>
                    <td>${user.agent_count || 0}</td>
                    <td>${user.phone_count || 0}</td>
                    <td>${user.kb_count || 0}</td>
                    <td>${user.call_count || 0}</td>
                    <td>${formatCurrency(user.total_amount || 0)}</td>
                    <td>${user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
          </html>
        `);
        
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (!isAdmin()) {
    // Get email from chatbotUser object (new format)
    const chatbotUser = localStorage.getItem('chatbotUser');
    let currentEmail = 'No email found';
    
    if (chatbotUser) {
      try {
        const user = JSON.parse(chatbotUser);
        currentEmail = user.email || 'No email found';
      } catch (e) {
        console.error('Error parsing chatbotUser:', e);
      }
    }
    
    // Fallback to old format (direct userEmail key)
    if (currentEmail === 'No email found') {
      currentEmail = localStorage.getItem('userEmail') || 'No email found';
    }
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: '50px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
          padding: '20px',
          minWidth: '900px',
          maxWidth: '1100px',
          width: '100%',
          color: '#111',
          marginTop: '0',
          marginBottom: '48px',
          height: '700px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>Admin Dashboard</h2>
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          color: '#dc2626',
          marginTop: '20px'
        }}>
          <h3>Access denied. Admin privileges required.</h3>
          <p>Current user email: <strong>{currentEmail}</strong></p>
          <p>Required email: <strong>info@urcommunity.com.au</strong></p>
          <p>Please login with the correct admin email to access this dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '50px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        padding: '20px',
        minWidth: '900px',
        maxWidth: '1100px',
        width: '100%',
        color: '#111',
        marginTop: '0',
        marginBottom: '48px',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
        <i className="fas fa-shield-alt" style={{ fontSize: '24px', color: '#dc2626', marginRight: '12px' }}></i>
        <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>
          Admin Dashboard
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          {/* Create an Agent Button - Admin Only */}
          <button
            onClick={() => setShowCreateAgentModal(true)}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: '600',
              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#059669';
              e.target.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#10b981';
              e.target.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
            }}
            title="Create an Agent"
          >
            <i className="fas fa-plus-circle"></i>
            Create an Agent
          </button>
          <button
            onClick={fetchAllUsersData}
            style={{
              background: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px'
            }}
            title="Refresh Data"
          >
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
          <button
            onClick={exportToCSV}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px'
            }}
            title="Export to CSV"
          >
            <i className="fas fa-download"></i>
            CSV
          </button>
          <button
            onClick={() => {
              // If user is selected with billing data (single month or 3 months), show popup
              if (selectedUserDetails && !showAllUsers && (billingData || (threeMonthView && threeMonthBillingData))) {
                setShowPDFPopup(true);
              } else {
                // Otherwise, directly export general dashboard PDF
                exportToPDF(false);
              }
            }}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px'
            }}
            title="Export to PDF"
          >
            <i className="fas fa-file-pdf"></i>
            PDF
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px',
          color: '#dc2626',
          marginBottom: '16px',
          fontSize: '14px',
          flexShrink: 0
        }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc2626',
              float: 'right',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* User Selection Section */}
      <div style={{
        background: '#f8f9fa',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        flexShrink: 0
      }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 12px 0' }}>
          Select User by Email
        </h4>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px', position: 'relative', display: 'flex' }} data-dropdown-container>
            <input
              type="text"
              placeholder="Search and select user by email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
                if (e.target.value === '') {
                  setSelectedUser(null);
                  setSelectedUserDetails(null);
                  setBillingData(null);
                }
              }}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filteredUsers.length > 0) {
                  handleUserSelect(filteredUsers[0].email);
                }
              }}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px 0 0 6px',
                fontSize: '14px',
                borderRight: 'none'
              }}
            />
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '0 6px 6px 0',
                background: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px'
              }}
            >
              <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'}`} style={{ fontSize: '12px', color: '#6b7280' }}></i>
            </button>
            {showDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {searchTerm ? (
                  // Show filtered results when typing
                  filteredUsers.length > 0 ? (
                    <>
                      {filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => handleUserSelect(user.email)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f3f4f6',
                            fontSize: '13px'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                        >
                          <div style={{ fontWeight: '500', color: '#1f2937' }}>{user.email}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {user.full_name || 'No name'} • {user.agent_count || 0} agents
                          </div>
                        </div>
                      ))}
                      <div
                        onClick={() => handleUserSelect('ALL')}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderTop: '2px solid #e5e7eb',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#7c3aed',
                          background: '#f8f9fa'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#ede9fe'}
                        onMouseLeave={(e) => e.target.style.background = '#f8f9fa'}
                      >
                        All
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                      No users found
                    </div>
                  )
                ) : (
                  // Show all users when dropdown is opened without typing
                  <>
                    {users.slice(0, 20).map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleUserSelect(user.email)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          fontSize: '13px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        <div style={{ fontWeight: '500', color: '#1f2937' }}>{user.email}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                          {user.full_name || 'No name'} • {user.agent_count || 0} agents
                        </div>
                      </div>
                    ))}
                    <div
                      onClick={() => handleUserSelect('ALL')}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderTop: '2px solid #e5e7eb',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#7c3aed',
                        background: '#f8f9fa'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#ede9fe'}
                      onMouseLeave={(e) => e.target.style.background = '#f8f9fa'}
                    >
                      All
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={month}
              onChange={(e) => {
                const selectedMonth = parseInt(e.target.value);
                console.log('🔍 [ADMIN-DASHBOARD] Month dropdown changed:', { selectedMonth, monthName: new Date(2000, selectedMonth - 1).toLocaleString('en-AU', { month: 'long' }) });
                setMonth(selectedMonth);
                // Clear billing data when month changes - user needs to click Submit
                setBillingData(null);
                setThreeMonthBillingData(null);
              }}
              style={{
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('en-AU', { month: 'long' })}</option>
              ))}
            </select>
            <input
              type="number"
              value={year}
              onChange={(e) => {
                setYear(parseInt(e.target.value));
                // Clear billing data when year changes - user needs to click Submit
                setBillingData(null);
                setThreeMonthBillingData(null);
              }}
              style={{
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
              <input
                type="checkbox"
                checked={threeMonthView}
                onChange={(e) => {
                  setThreeMonthView(e.target.checked);
                  // Clear data when toggling
                  setBillingData(null);
                  setThreeMonthBillingData(null);
                }}
                style={{ cursor: 'pointer' }}
              />
              <span>3 Months View</span>
            </label>
            <button
              onClick={() => {
                console.log('🔍 [ADMIN-DASHBOARD] Submit button clicked:', { searchTerm, showAllUsers, month, year });
                
                // If "ALL" is selected, fetch all users billing
                if (searchTerm === 'ALL' || showAllUsers) {
                  setShowAllUsers(true);
                  setSelectedUser(null);
                  setSelectedUserDetails(null);
                  fetchAllUsersBilling();
                  return;
                }
                
                // If a user email is selected or entered
                if (searchTerm && searchTerm !== 'ALL') {
                  // Find the user from the users list
                  const user = users.find(u => u.email.toLowerCase() === searchTerm.toLowerCase());
                  if (user) {
                    setSelectedUser(user);
                    setShowAllUsers(false);
                    // If 3-month view is enabled, fetch 3 months data
                    if (threeMonthView) {
                      fetchThreeMonthsBilling(user.email);
                      // Also set user details
                      setSelectedUserDetails({
                        id: user.id,
                        email: user.email,
                        fullName: user.full_name || 'N/A',
                        phone: user.phone || 'N/A',
                        createdAt: user.created_at,
                        agentCount: user.agent_count || 0,
                        phoneCount: user.phone_count || 0,
                        kbCount: user.kb_count || 0,
                        callCount: user.call_count || 0
                      });
                    } else {
                      fetchUserDetailsAndBilling(user.email);
                    }
                  } else {
                    // Try to fetch even if user not found in list (might be valid email)
                    setShowAllUsers(false);
                    if (threeMonthView) {
                      fetchThreeMonthsBilling(searchTerm);
                    } else {
                      fetchUserDetailsAndBilling(searchTerm);
                    }
                  }
                } else {
                  alert('Please select a user or "ALL" before submitting.');
                }
              }}
              style={{
                padding: '10px 20px',
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#6d28d9'}
              onMouseLeave={(e) => e.target.style.background = '#7c3aed'}
            >
              <i className="fas fa-search"></i>
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* User Details and Billing Section */}
      {(selectedUserDetails || showAllUsers) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
          {/* User Details Card or All Users Header */}
          {showAllUsers ? (
            <div style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                All Users - Combined Billing
              </h4>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Combined billing data for all users for the selected period
              </div>
            </div>
          ) : (
            <div style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                User Details
              </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>User ID</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', wordBreak: 'break-all' }}>
                  {selectedUserDetails.id}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Email</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  {selectedUserDetails.email}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Full Name</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  {selectedUserDetails.fullName}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Joined Date</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  {formatDate(selectedUserDetails.createdAt)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Agents</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  {selectedUserDetails.agentCount}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Phone Numbers</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  {selectedUserDetails.phoneCount}
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Billing Data */}
          {!threeMonthView && billingLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#7c3aed' }}></i>
            </div>
          ) : !threeMonthView && billingError ? (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              color: '#dc2626'
            }}>
              Error loading billing data: {billingError}
            </div>
          ) : !threeMonthView && billingData ? (
            <>
              {/* Billing Period */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Billing Period</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                  {billingData.billingPeriod?.start} to {billingData.billingPeriod?.end}
                </div>
              </div>

              {/* Component A, B, C Summary */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: '12px',
                padding: '20px'
              }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                  Billing Summary
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  {/* Component A */}
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                      Component A (Voice Calls)
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Original:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                        {formatCurrency(billingData.original?.componentA || 0)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Doubled:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                        {formatCurrency(billingData.doubled?.componentA || 0)}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                      {billingData.counts?.calls || 0} calls
                    </div>
                  </div>

                  {/* Component B */}
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                      Component B (Chat Sessions)
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Original:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                        {formatCurrency(billingData.original?.componentB || 0)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Doubled:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                        {formatCurrency(billingData.doubled?.componentB || 0)}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                      {billingData.counts?.chats || 0} chats
                    </div>
                  </div>

                  {/* Component C */}
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                      Component C (Phone Numbers)
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Original:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                        {formatCurrency(billingData.original?.componentC || 0)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Doubled:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                        {formatCurrency(billingData.doubled?.componentC || 0)}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                      {billingData.counts?.phones || 0} phones
                    </div>
                  </div>

                  {/* Total */}
                  <div style={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    borderRadius: '8px',
                    padding: '16px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                      Total
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', opacity: 0.9 }}>Original:</span>
                      <span style={{ fontSize: '16px', fontWeight: '600' }}>
                        {formatCurrency(billingData.original?.total || 0)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', opacity: 0.9 }}>Doubled:</span>
                      <span style={{ fontSize: '16px', fontWeight: '600' }}>
                        {formatCurrency(billingData.doubled?.total || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Billing Status */}
                  <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                      Billing Status
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: (billingData.billingStatus === 'PAID' || billingData.billingStatus === 'paid') ? '#10b981' : '#f59e0b',
                        textTransform: 'capitalize'
                      }}>
                        {billingData.billingStatus || 'Pending'}
                      </span>
                      {(billingData.billingStatus === 'PAID' || billingData.billingStatus === 'paid') && billingData.paymentIntentId && (
                        <button
                          onClick={() => {
                            // Open Stripe invoice in new tab using payment intent ID
                            const stripeInvoiceUrl = `https://dashboard.stripe.com/payments/${billingData.paymentIntentId}`;
                            window.open(stripeInvoiceUrl, '_blank');
                          }}
                          style={{
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <i className="fas fa-file-invoice"></i>
                          Invoice
                        </button>
                      )}
                    </div>
                    {billingData.paymentIntentId && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        Payment ID: {billingData.paymentIntentId.substring(0, 20)}...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Component A Internal Breakdown */}
              {billingData.componentAInternal && (
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                    Component A Internal Breakdown
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {Object.entries(billingData.componentAInternal.original).map(([key, value]) => (
                      <div key={key} style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '8px', textTransform: 'capitalize' }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>Original:</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#1f2937' }}>
                            {formatCurrency(value || 0)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>Doubled:</span>
                          <span style={{ fontSize: '12px', fontWeight: '500', color: '#7c3aed' }}>
                            {formatCurrency(billingData.componentAInternal.doubled[key] || 0)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}

          {/* 3 Months View Section */}
          {threeMonthView && selectedUserDetails && !showAllUsers && (
            <>
              {threeMonthLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '12px' }}></i>
                  <div>Loading 3 months billing data...</div>
                </div>
              ) : threeMonthBillingData && threeMonthBillingData.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#1f2937', margin: '0' }}>
                    3 Months Billing Summary
                  </h3>
                  
                  {threeMonthBillingData.map((monthData, index) => (
                    <div key={index} style={{
                      background: '#f8f9fa',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                        {monthData.monthName} {monthData.year}
                        {monthData.data?.billingPeriod && (
                          <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280', marginLeft: '8px' }}>
                            ({monthData.data.billingPeriod.start} to {monthData.data.billingPeriod.end})
                          </span>
                        )}
                      </h4>
                      
                      {monthData.success && monthData.data ? (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                            {/* Component A */}
                            <div style={{
                              background: 'white',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                                Component A (Voice Calls)
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Original:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                                  {formatCurrency(monthData.data.original?.componentA || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Doubled:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                                  {formatCurrency(monthData.data.doubled?.componentA || 0)}
                                </span>
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                                {monthData.data.counts?.calls || 0} calls
                              </div>
                            </div>

                            {/* Component B */}
                            <div style={{
                              background: 'white',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                                Component B (Chat Sessions)
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Original:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                                  {formatCurrency(monthData.data.original?.componentB || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Doubled:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                                  {formatCurrency(monthData.data.doubled?.componentB || 0)}
                                </span>
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                                {monthData.data.counts?.chats || 0} chats
                              </div>
                            </div>

                            {/* Component C */}
                            <div style={{
                              background: 'white',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                                Component C (Phone Numbers)
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Original:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                                  {formatCurrency(monthData.data.original?.componentC || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>Doubled:</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                                  {formatCurrency(monthData.data.doubled?.componentC || 0)}
                                </span>
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
                                {monthData.data.counts?.phones || 0} phones
                              </div>
                            </div>

                            {/* Total */}
                            <div style={{
                              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                              borderRadius: '8px',
                              padding: '16px',
                              color: 'white'
                            }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                                Total
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', opacity: 0.9 }}>Original:</span>
                                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                  {formatCurrency(monthData.data.original?.total || 0)}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', opacity: 0.9 }}>Doubled:</span>
                                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                  {formatCurrency(monthData.data.doubled?.total || 0)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Component A Internal Breakdown */}
                          {monthData.data.componentAInternal && (
                            <div style={{
                              background: '#f8f9fa',
                              borderRadius: '12px',
                              padding: '20px',
                              marginTop: '16px'
                            }}>
                              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 16px 0' }}>
                                Voice Calls Internal Billing Breakdown
                              </h4>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                {Object.entries(monthData.data.componentAInternal.original).map(([key, value]) => (
                                  <div key={key} style={{
                                    background: 'white',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', marginBottom: '8px', textTransform: 'capitalize' }}>
                                      {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                      <span style={{ fontSize: '11px', color: '#6b7280' }}>Original:</span>
                                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#1f2937' }}>
                                        {formatCurrency(value || 0)}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ fontSize: '11px', color: '#6b7280' }}>Doubled:</span>
                                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#7c3aed' }}>
                                        {formatCurrency(monthData.data.componentAInternal.doubled[key] || 0)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ padding: '16px', background: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: '14px' }}>
                          {monthData.error || 'No data available for this month'}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* 3 Months Total Summary */}
                  {threeMonthBillingData.every(m => m.success && m.data) && (
                    <div style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      borderRadius: '12px',
                      padding: '20px',
                      color: 'white'
                    }}>
                      <h4 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0 0 16px 0' }}>
                        3 Months Total Summary
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        <div>
                          <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Total Original (3 Months)</div>
                          <div style={{ fontSize: '20px', fontWeight: '600' }}>
                            {formatCurrency(
                              threeMonthBillingData.reduce((sum, m) => sum + (m.data?.original?.total || 0), 0)
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Total Doubled (3 Months)</div>
                          <div style={{ fontSize: '20px', fontWeight: '600' }}>
                            {formatCurrency(
                              threeMonthBillingData.reduce((sum, m) => sum + (m.data?.doubled?.total || 0), 0)
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : threeMonthBillingData && threeMonthBillingData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No billing data available for 3 months view.
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* PDF Export Popup */}
      {showPDFPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPDFPopup(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Export PDF
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Choose the type of PDF you want to export:
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowPDFPopup(false);
                  exportToPDF(true); // For User
                }}
                style={{
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#6d28d9'}
                onMouseLeave={(e) => e.target.style.background = '#7c3aed'}
              >
                <i className="fas fa-user"></i>
                User
              </button>
              <button
                onClick={() => {
                  setShowPDFPopup(false);
                  exportToPDF(false); // For Myself
                }}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
              >
                <i className="fas fa-user-circle"></i>
                Myself
              </button>
              <button
                onClick={() => setShowPDFPopup(false)}
                style={{
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.background = '#d1d5db'}
                onMouseLeave={(e) => e.target.style.background = '#e5e7eb'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Agent Modal - Full screen modal for creating new agents */}
      {showCreateAgentModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div 
            style={{
              background: '#fff',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              width: '95vw',
              maxWidth: '1200px',
              height: '95vh',
              maxHeight: '800px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowCreateAgentModal(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 22,
                color: '#374151',
                cursor: 'pointer',
                zIndex: 10
              }}
              aria-label="Close"
            >
              ×
            </button>
            
            {/* Create Agent Form Container */}
            <div 
              style={{
                flex: 1,
                overflow: 'auto'
              }}
            >
              <CreateAgentForm 
                onClose={() => setShowCreateAgentModal(false)}
                onSuccess={(data) => {
                  // Handle successful agent creation
                  console.log('Agent created successfully:', data);
                  setShowCreateAgentModal(false);
                  // Refresh the dashboard data
                  fetchAllUsersData();
                }}
                userEmail={userEmail}
                userId={finalUserId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;